import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../auth/useAuth";
import {
  deleteRequest,
  getErrorMessage,
  getRequest,
  MANUFACTURING_API,
  patchRequest,
  postRequest,
} from "./api";
import {
  EmptyStateCard,
  PageShell,
  SectionCard,
  panelSx,
  statusChip,
  controlDeskSx,
  metricTileSx
} from "./ManufacturingCommon";

const emptyForm = {
  name: "",
  code: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  status: "ACTIVE",
  notes: "",
};

function ManufacturingPartnersSkeleton() {
  const cardSkeletons = Array.from({ length: 4 });
  const fieldSkeletons = Array.from({ length: 4 });

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {cardSkeletons.map((_, index) => (
        <Box sx={{ width: "100%" }} key={index}>
          <Box sx={{ ...panelSx, overflow: "hidden" }}>
            <Box sx={{ p: 2.4, borderBottom: "1px solid rgba(226,232,240,0.9)" }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="34%" height={30} />
                  <Skeleton variant="text" width="18%" height={22} />
                </Box>
                <Skeleton variant="rounded" width={92} height={26} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "minmax(0, 1fr) 280px",
                },
                minHeight: 188,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {fieldSkeletons.map((__, fieldIndex) => (
                    <Box
                      key={fieldIndex}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid rgba(226,232,240,0.95)",
                      }}
                    >
                      <Skeleton variant="text" width="44%" height={18} />
                      <Skeleton variant="text" width="72%" height={24} sx={{ mt: 0.85 }} />
                    </Box>
                  ))}
                </Box>
                <Skeleton variant="rounded" width="100%" height={70} sx={{ mt: 1.5, borderRadius: 2.5 }} />
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderTop: { xs: "1px solid rgba(226,232,240,0.9)", xl: "none" },
                  borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                  backgroundColor: "#F8FAFC",
                }}
              >
                <Skeleton variant="text" width="38%" height={18} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 2, borderRadius: 2 }} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 1, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Grid>
  );
}

export default function ManufacturingPartners() {
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  // const [deleting, setDeleting] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  

  const [errors, setErrors] = useState({});
   const activeCount = partners.filter(
    (partner) => (partner.status || "ACTIVE") === "ACTIVE",
  ).length;

  const loadPartners = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${MANUFACTURING_API}/partners`,
        token,
        activeOrg,
      );
      setPartners(data?.partners || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing partners"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (partner) => {
    setEditingId(partner.id);
    setForm({
      name: partner.name || "",
      code: partner.code || "",
      contact_name: partner.contact_name || "",
      contact_email: partner.contact_email || "",
      contact_phone: partner.contact_phone || "",
      status: partner.status || "ACTIVE",
      notes: partner.notes || "",
    });
    setErrors({});
    setOpen(true);
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Required";
    if (!form.code.trim()) newErrors.code = "Required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    // setError("");

    try {
      if (editingId) {
        await patchRequest(
          `${MANUFACTURING_API}/partners/${editingId}`,
          form,
          token,
          activeOrg,
        );
        setSuccess("Manufacturing partner updated");
      } else {
        await postRequest(
          `${MANUFACTURING_API}/partners`,
          form,
          token,
          activeOrg,
        );
        setSuccess("Manufacturing partner created");
      }

      setOpen(false);
      await loadPartners();
    setErrors({});
  } catch (err) {
    const message = err?.response?.data?.error || "";

    // ✅ Map backend error to fields
    if (message.toLowerCase().includes("name")) {
      setErrors((prev) => ({
        ...prev,
        name: "Partner name already exists",
      }));
    } else if (message.toLowerCase().includes("code")) {
      setErrors((prev) => ({
        ...prev,
        code: "Partner code already exists",
      }));
    } else {
      // fallback generic error
      setErrors((prev) => ({
        ...prev,
        name: message || "Something went wrong",
      }));
    }
  } finally {
    setSaving(false);
  }
  };

  const handleDelete = (partner) => {
    setSelectedPartner(partner);
    setDeleteOpen(true);
  };
  const confirmDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteRequest(
        `${MANUFACTURING_API}/partners/${selectedPartner.id}`,
        token,
        activeOrg
      );
      setSuccess("Manufacturing partner deleted");
      setDeleteOpen(false);
      await loadPartners();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete manufacturing partner"));
    }
    finally {
      setLoadingDelete(false);
    }
  };

  return (
    <PageShell
      title="Manufacturing Partners"
      subtitle="Manage EMS, SMT, and assembly partners who build products against TrackCoreData designs."
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
        >
          Add Partner
        </Button>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={controlDeskSx}>
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", xl: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: {xs:10,md:15}, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
              Partner Network
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 15, color: "#334155", lineHeight: 1.7 }}>
              Qualified vendor records with contact data and execution state in one clean operational roster.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ minWidth: { xl: 420 } }}>
            {[
              ["Total partners", partners.length],
              ["Active", activeCount],
              ["Inactive", Math.max(0, partners.length - activeCount)],
            ].map(([label, value]) => (
              <Box key={label} sx={{ ...metricTileSx, flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {label}
                </Typography>
                <Typography sx={{ mt: 0.65, fontSize: 24, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.04em" }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Paper>





      {loading ? (
        <ManufacturingPartnersSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {partners.map((partner) => (
            <Box sx={{ width: "100%" }} key={partner.id}>
              <SectionCard
                title={partner.name}
                subtitle={partner.code}
                action={statusChip(partner.status)}
                contentSx={{
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      xl: "minmax(0, 1fr) 280px",
                    },
                    minHeight: 188,
                  }}
                >
                  <Box sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1.25,
                      }}
                    >
                      {[
                        ["Contact", partner.contact_name || "Not set"],
                        ["Email", partner.contact_email || "Not set"],
                        ["Phone", partner.contact_phone || "Not set"],
                        ["Status", partner.status || "ACTIVE"],
                      ].map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: "#F8FAFC",
                            border: "1px solid rgba(226,232,240,0.95)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#64748B",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.85,
                              fontSize: 15,
                              fontWeight: 800,
                              color: "#0F172A",
                              wordBreak: "break-word",
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {partner.notes ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 2.5,
                          backgroundColor: "#F8FAFC",
                          border: "1px solid rgba(226,232,240,0.95)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: "#64748B",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Notes
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.85,
                            fontSize: 14,
                            color: "#334155",
                            lineHeight: 1.7,
                          }}
                        >
                          {partner.notes}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>

                  <Box
                    sx={{
                      p: 2.5,
                      borderTop: {
                        xs: "1px solid rgba(226,232,240,0.9)",
                        xl: "none",
                      },
                      borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                      backgroundColor: "#F8FAFC",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#64748B",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Actions
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: 14,
                        color: "#475569",
                        lineHeight: 1.7,
                      }}
                    >
                      Update the vendor profile or remove this partner from the
                      approved manufacturing roster.
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row", xl: "column" }} spacing={1.2} sx={{ mt: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleEdit(partner)}
                        sx={{ fontWeight: 800, width: { xs: "100%", md: 220 } }}
                      >
                        Edit Partner
                      </Button>
                      <Button
                        fullWidth
                        color="error"
                        variant="outlined"
                        onClick={() => handleDelete(partner)}
                        sx={{ fontWeight: 800, width: { xs: "100%", md: 220 } }}
                      >
                        Delete Partner
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </SectionCard>
            </Box>
          ))}
          {!partners.length ? (
            <Grid item xs={12}>
              <EmptyStateCard
                title="No manufacturing partners yet"
                description="Add the first contract manufacturer or EMS partner to start raising production orders."
              />
            </Grid>
          ) : null}
        </Grid>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
            pr: 5,
          }}
        >
          {editingId
            ? "Edit Manufacturing Partner"
            : "Create Manufacturing Partner"}

          {/* CLOSE ICON */}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#F8FAFC",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Partner Name"
              required
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                setErrors((prev) => ({ ...prev, name: "" }));
              }}
              error={!!errors.name}
              helperText={errors.name || ""}
              fullWidth
            />

            <TextField
              label="Partner Code"
              required
              value={form.code}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, code: e.target.value }));
                setErrors((prev) => ({ ...prev, code: "" }));
              }}
              error={!!errors.code}
              helperText={errors.code || ""}
              fullWidth
            />

            <TextField
              label="Contact Name"
              value={form.contact_name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  contact_name: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Contact Email"
              value={form.contact_email}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  contact_email: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              label="Contact Phone"
              value={form.contact_phone}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  contact_phone: e.target.value,
                }))
              }
              fullWidth
            />

            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>

            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpen(false);
              setErrors({});
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              minWidth: 100, // 👈 prevents button shrink
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : editingId ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to Delete this?{" "}

          </Typography>
        </DialogContent>

        <DialogActions>

          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>

          <Button
            color="error"
            variant="contained"
            disabled={loadingDelete}
            onClick={confirmDelete}
          >
            {loadingDelete ? <CircularProgress size={16} /> : "YES, DELETE"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
